<script lang="ts">
  // The Share affordance (Story 5.2, AC 1/2; AD-2, AD-4, AD-7, AD-8). A keyboard-
  // operable Copy Link button that obtains the shareable URL from Story 5.1's codec
  // (the sole URL owner) via the threaded `buildShareUrl` callback, copies it through
  // the threaded `platform` clipboard adapter, announces the result via the polite
  // live region, and reveals a labelled read-only selectable fallback field when the
  // clipboard is unavailable/denied. `ui` imports NEITHER `share` NOR `platform`: both
  // side-effecting seams arrive as props from the composition root (like `resolve`),
  // so the URL/clipboard effects stay in `app`/`platform` (AD-2). Activating Share
  // dispatches NO command and mutates NO store field; fallback visibility and the last
  // built URL are component-local `$state`, not store state.
  // Share reads NO store field directly: the current config is captured entirely by
  // `buildShareUrl` (5.1's URL owner, bound to the live store in the composition
  // root), so this component stays free of any `state`/`share`/`platform` import.
  let {
    announce,
    buildShareUrl,
    copyText
  }: {
    announce?: (message: string) => void;
    buildShareUrl: () => string;
    copyText: (text: string) => Promise<boolean>;
  } = $props();

  // Mass-neutral copy (EXPERIENCE.md Voice table); success uses the NEUTRAL polite
  // channel, never the assertive one reserved for collision/absorption.
  const SUCCESS_MESSAGE = "Link copied. It's ready to share.";
  const FALLBACK_MESSAGE = "Couldn't reach the clipboard. Copy the link below.";

  let fallbackVisible = $state(false);
  let shareUrl = $state('');
  let copied = $state(false);
  let fieldEl = $state<HTMLInputElement | null>(null);

  async function onShare(): Promise<void> {
    // Build the URL first: this triggers 5.1's encode + fragment write, so
    // `location.hash` and the copied string agree (AD-4). No store mutation.
    const url = buildShareUrl();
    shareUrl = url;
    const ok = await copyText(url);
    if (ok) {
      fallbackVisible = false;
      copied = true;
      announce?.(SUCCESS_MESSAGE);
    } else {
      // Reveal the guaranteed path: the selectable read-only field (AD-7). No throw,
      // no modal. Focus + select it so the learner can copy by hand immediately.
      copied = false;
      fallbackVisible = true;
      announce?.(FALLBACK_MESSAGE);
      await Promise.resolve();
      fieldEl?.focus();
      fieldEl?.select();
    }
  }

  function selectAll(): void {
    fieldEl?.select();
  }
</script>

<div class="share-control">
  <button
    type="button"
    class="share-btn focus-ring"
    data-testid="share-button"
    onclick={onShare}
  >
    <span class="material-symbols-outlined" aria-hidden="true">link</span>
    <span class="share-btn-text">Copy link</span>
  </button>

  {#if copied && !fallbackVisible}
    <!-- Non-blocking inline success cue (text, not colour-only). The authoritative
         success announcement goes through the polite live region (AD-8). -->
    <span class="share-copied" data-testid="share-copied" role="presentation">Copied</span>
  {/if}

  <div
    class="share-fallback glass-panel"
    data-testid="share-fallback"
    hidden={!fallbackVisible}
  >
    <label class="share-fallback-label" for="share-url-field">Copy the link below.</label>
    <input
      id="share-url-field"
      class="share-url-field data-mono"
      type="text"
      readonly
      data-testid="share-url-field"
      value={shareUrl}
      bind:this={fieldEl}
      onfocus={selectAll}
    />
  </div>
</div>

<style>
  .share-control {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    position: relative;
  }

  .share-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: none;
    border-radius: var(--radius-full);
    background: rgba(255, 255, 255, 0.08);
    color: var(--on-surface);
    font-size: 14px;
    cursor: pointer;
  }

  .share-btn:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  .share-btn .material-symbols-outlined {
    font-size: 20px;
  }

  .share-copied {
    font-size: 13px;
    color: var(--on-surface-variant);
  }

  .share-fallback {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: min(420px, 70vw);
    padding: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    border-left: 1px solid rgba(255, 255, 255, 0.15);
  }

  .share-fallback[hidden] {
    display: none;
  }

  .share-fallback-label {
    font-size: 13px;
    color: var(--on-surface-variant);
  }

  .share-url-field {
    width: 100%;
    padding: 8px 10px;
    border-radius: var(--radius-default);
    border: 1px solid var(--outline-variant, #414751);
    background: rgba(255, 255, 255, 0.04);
    color: var(--on-surface);
    font-size: 13px;
  }

  .share-url-field:focus-visible {
    outline: 2px solid #d4e3ff;
    outline-offset: 2px;
  }
</style>
